/**
 * Newsletter API Logic
 * Functions to load newsletters from the newsletters subdirectory structure
 * and handle newsletter subscription
 */

import { getAllNewsletters, getNewsletterBySlug, getNewsletterContent } from '../newsletters';

/**
 * Load all newsletters
 * @param {string} language - Language code (en, de, etc.)
 * @returns {Promise<{success: boolean, data: Array}>}
 */
export async function loadAllNewsletters(language = 'en') {
  try {
    const allNewsletters = getAllNewsletters();
    
    // Enrich each newsletter with content for the specified language
    const newslettersWithContent = allNewsletters.map(newsletter => {
      const content = getNewsletterContent(newsletter.slug, language);
      return {
        ...newsletter,
        ...content
      };
    });

    return {
      success: true,
      data: newslettersWithContent
    };
  } catch (error) {
    console.error('Error loading newsletters:', error);
    return {
      success: false,
      data: [],
      error: error.message
    };
  }
}

/**
 * Load a specific newsletter by slug
 * @param {string} slug - Newsletter slug
 * @param {string} language - Language code (en, de, etc.)
 * @returns {Promise<{success: boolean, data: object | null}>}
 */
export async function loadNewsletter(slug, language = 'en') {
  try {
    const newsletter = getNewsletterBySlug(slug, language);
    
    if (!newsletter) {
      return {
        success: false,
        data: null,
        error: 'Newsletter not found'
      };
    }

    return {
      success: true,
      data: newsletter
    };
  } catch (error) {
    console.error('Error loading newsletter:', error);
    return {
      success: false,
      data: null,
      error: error.message
    };
  }
}

/**
 * Subscribe to newsletter
 * @param {object} subscriptionData - Subscription data
 * @param {object} apiClient - API client instance
 * @returns {Promise<{success: boolean, data: object | null, error: Error | null}>}
 */
export async function subscribeNewsletter(subscriptionData, apiClient = null) {
  try {
    if (!apiClient) {
      apiClient = await import('utils/apiClient').then(m => m.getApiClient());
    }

    // TODO: Implement actual newsletter subscription API call
    const response = await apiClient.post('/v1/newsletter/subscribe', subscriptionData);

    if (response && response.success !== false) {
      return {
        success: true,
        data: response.data,
        error: null
      };
    } else {
      throw new Error(response.error || 'Failed to subscribe to newsletter');
    }
  } catch (err) {
    console.error('Error subscribing to newsletter:', err);
    return {
      success: false,
      data: null,
      error: err instanceof Error ? err : new Error(err.message || 'Failed to subscribe to newsletter')
    };
  }
}


